"use strict";
exports.__esModule = true;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var react_hooks_1 = require("@onemedical/react-hooks");
var is_url_1 = tslib_1.__importDefault(require("is-url"));
var js_base64_1 = require("js-base64");
var prop_types_1 = tslib_1.__importDefault(require("prop-types"));
var randombytes_1 = tslib_1.__importDefault(require("randombytes"));
var sha_js_1 = tslib_1.__importDefault(require("sha.js"));
var AuthenticationContext_1 = tslib_1.__importDefault(require("./AuthenticationContext"));
function getToken() {
    return window.localStorage.getItem('auth:token');
}
function AuthenticationProvider(_a) {
    var _this = this;
    var providerUrl = _a.providerUrl, clientID = _a.clientID, authorizePath = _a.authorizePath, callbackPath = _a.callbackPath, logoutPath = _a.logoutPath, audience = _a.audience, scope = _a.scope, auth0Connection = _a.auth0Connection, auth0LogoutReturnTo = _a.auth0LogoutReturnTo, children = _a.children;
    var _b = react_1.useState({
        authenticated: !!getToken(),
        authenticating: false,
        error: undefined
    }), state = _b[0], setState = _b[1];
    var setError = react_hooks_1.useEventCallback(function (description) {
        setState({ authenticated: false, authenticating: false, error: description });
    });
    var fetchToken = react_hooks_1.useEventCallback(function (code) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
        var verifier, response, json;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    verifier = window.localStorage.getItem('auth:verifier');
                    return [4 /*yield*/, fetch(providerUrl + "/oauth/token", {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Accept: 'application/json'
                            },
                            body: JSON.stringify({
                                grant_type: 'authorization_code',
                                client_id: clientID,
                                redirect_uri: "" + window.location.origin + callbackPath,
                                code_verifier: verifier,
                                code: code
                            })
                        })];
                case 1:
                    response = _a.sent();
                    return [4 /*yield*/, response.json()];
                case 2:
                    json = _a.sent();
                    if (json.error) {
                        console.error('[authentication error]', json.error_description); // eslint-disable-line no-console
                        setError(json.error_description);
                        return [2 /*return*/, false];
                    }
                    window.localStorage.setItem('auth:token', json.access_token);
                    setState({ authenticated: true, authenticating: false, error: undefined });
                    return [2 /*return*/, true];
            }
        });
    }); });
    var authorize = react_hooks_1.useEventCallback(function (referrer) {
        setState({ authenticated: false, authenticating: true, error: undefined });
        window.localStorage.clear();
        var uniqueVerifier = js_base64_1.Base64.fromUint8Array(randombytes_1["default"](32), true);
        window.localStorage.setItem('auth:verifier', uniqueVerifier);
        var encodedReferrer = encodeURIComponent("" + referrer.pathname + referrer.search + referrer.hash);
        var authorizeUrl = "" + providerUrl + authorizePath +
            ("?redirect_uri=" + window.location.origin + callbackPath) +
            '&protocol=oauth2' +
            '&response_type=code' +
            '&response_mode=query' +
            ("&code_challenge=" + js_base64_1.Base64.fromUint8Array(sha_js_1["default"]('sha256').update(uniqueVerifier).digest(), true)) +
            '&code_challenge_method=S256' +
            ("&state=" + encodedReferrer) +
            ("&client_id=" + clientID) +
            '&auth0Client=eyJuYW1lIjoiYXV0aDAtcmVhY3QiLCJ2ZXJzaW9uIjoiMS4xLjAifQ%3D%3D';
        if (audience)
            authorizeUrl += "&audience=" + audience;
        if (scope)
            authorizeUrl += "&scope=" + scope;
        if (auth0Connection)
            authorizeUrl += "&connection=" + auth0Connection;
        window.location.assign(authorizeUrl);
    });
    var logout = react_hooks_1.useEventCallback(function () {
        window.localStorage.clear();
        window.location.assign("" + providerUrl + logoutPath +
            ("?returnTo=" + encodeURIComponent(auth0LogoutReturnTo || window.location.origin) + "&client_id=" + clientID));
    });
    return (react_1["default"].createElement(AuthenticationContext_1["default"].Provider, { value: tslib_1.__assign({ callbackPath: callbackPath,
            authorize: authorize,
            fetchToken: fetchToken,
            logout: logout,
            getToken: getToken,
            setError: setError }, state) }, children));
}
function createUrlPropType(isRequired) {
    return function (props, propName, componentName) {
        var value = props[propName]; // eslint-disable-line react/destructuring-assignment
        if (!value) {
            if (isRequired) {
                return new TypeError("Missing '" + propName + "' on '" + componentName + "'");
            }
            // prop is optional. do nothing.
        }
        else if (typeof value !== 'string' || !is_url_1["default"](value)) {
            return new TypeError("Invalid prop '" + propName + "' passed to '" + componentName + "'. Expected a valid URL.");
        }
        return null;
    };
}
var urlPropType = createUrlPropType(false);
urlPropType.isRequired = createUrlPropType(true);
function urlPathPropType(props, propName, componentName) {
    return (/^(\/[-a-z\d%_.~+]*)*$/.test(props[propName])
        ? null
        : new TypeError("Invalid prop '" + propName + "' passed to '" + componentName + "'. Expected a valid URL path."));
}
AuthenticationProvider.propTypes = {
    /** A URL pointing to an OAuth 2.0 server */
    providerUrl: urlPropType.isRequired,
    /** The OAuth 2.0 client ID assigned to your React application */
    clientID: prop_types_1["default"].string.isRequired,
    /** The URL path on which your OAuth server receives authorization requests. Defaults to Auth0's `/authorize` path.
     * When using onelife as your OAuth provider set this prop to `/oauth/authorize`. */
    authorizePath: urlPathPropType,
    /** A URL path on which your app will listen for OAuth callbacks. Along with the protocol and hostname this
     * constitutes the client redirect URI that needs to be registered with the OAuth provider. Defaults to `/`. */
    callbackPath: urlPathPropType,
    /** The OAuth provider's logout path. Defaults to Auth0's `/v2/logout` path. When using onelife as your OAuth
     * provider set this prop to `/admin/oauth/logout`. */
    logoutPath: urlPathPropType,
    /** An optional OAuth audience */
    audience: prop_types_1["default"].string,
    /** An optional OAuth scope */
    scope: prop_types_1["default"].string,
    /** An optional Auth0 connection. See https://auth0.com/docs/identityproviders for more info. */
    auth0Connection: prop_types_1["default"].string,
    /** The URL the user gets redirected to after logging out from Auth0. Defaults to your application's root URL. */
    auth0LogoutReturnTo: urlPropType,
    children: prop_types_1["default"].node.isRequired
};
AuthenticationProvider.defaultProps = {
    authorizePath: '/authorize',
    callbackPath: '/',
    logoutPath: '/v2/logout',
    audience: undefined,
    scope: undefined,
    auth0Connection: undefined,
    auth0LogoutReturnTo: undefined
};
exports["default"] = AuthenticationProvider;
//# sourceMappingURL=AuthenticationProvider.js.map